/**
 * Cart Upsell Manager - Handles inline upsell content within cart sections
 * Manages state transitions between normal cart view and upsell content
 */

(function() {
  'use strict';

  class CartUpsellManager {
    constructor() {
      this.activeUpsells = new Map(); // Track active upsells by section ID
      this.init();
    }

    init() {
      this.bindEvents();
      this.checkDebugMode();
      // console.log('🛍️ [DEBUG] Cart Upsell Manager initialized');
      // console.log('🛍️ [DEBUG] Available methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(this)));
    }

    /**
     * Check for debug_modal query parameter and auto-enable upsell
     */
    checkDebugMode() {
      const urlParams = new URLSearchParams(window.location.search);
      const debugModal = urlParams.get('debug_modal');
      
      if (debugModal) {
        // console.log('🛍️ [DEBUG] debug_modal query parameter detected, auto-enabling upsell');
        
        // Wait for DOM to be fully ready and cart drawer to be available
        setTimeout(() => {
          this.autoEnableDebugUpsell();
        }, 1000);
      }
    }

    /**
     * Auto-enable upsell for debug purposes
     */
    autoEnableDebugUpsell() {
      // Try to find a section ID from the cart drawer
      const cartDrawer = document.getElementById('CartDrawer');
      if (!cartDrawer) {
        // console.warn('🛍️ [DEBUG] Cart drawer not found, cannot auto-enable upsell');
        return;
      }

      const sectionId = cartDrawer.dataset.sectionId;
      if (!sectionId) {
        // console.warn('🛍️ [DEBUG] Section ID not found, cannot auto-enable upsell');
        return;
      }

      // console.log('🛍️ [DEBUG] Auto-enabling upsell for section:', sectionId);

      // Check if upsell content exists
      const upsellContent = document.getElementById(`buno-plus-upsell-content-${sectionId}`);
      const upsellSidebar = document.getElementById(`buno-plus-upsell-sidebar-${sectionId}`);
      
      if (!upsellContent && !upsellSidebar) {
        // console.warn('🛍️ [DEBUG] No upsell content found for section:', sectionId);
        return;
      }

      // Open cart drawer first if it's not already open
      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show();
        
        // Wait for cart drawer to open, then show upsell
        setTimeout(() => {
          this.showUpsell(sectionId, 'cart-drawer');
        }, 500);
      } else {
        // If cart drawer is already open or doesn't have show method, show upsell directly
        this.showUpsell(sectionId, 'cart-drawer');
      }
    }

    bindEvents() {
      // Listen for upsell show requests
      document.addEventListener('cart-upsell:show', this.handleShowUpsell.bind(this));
      
      // Listen for upsell hide requests
      document.addEventListener('cart-upsell:hide', this.handleHideUpsell.bind(this));
      
      // Listen for back button clicks
      document.addEventListener('click', (event) => {
        const backButton = event.target.closest('[data-action="back-to-cart"]');
        if (backButton) {
          event.preventDefault();
          const sectionId = backButton.dataset.sectionId;
          if (sectionId) {
            this.hideUpsell(sectionId);
          }
        }
      });

      // Listen for upsell button clicks
      document.addEventListener('click', (event) => {
        const button = event.target.closest('.upsell-button');
        if (!button) return;

        event.preventDefault();
        this.handleUpsellAction(button);
      });

      // Listen for sidebar close button clicks
      document.addEventListener('click', (event) => {
        const closeButton = event.target.closest('.buno-plus-upsell-sidebar .drawer__close');
        if (closeButton) {
          event.preventDefault();
          const sidebarElement = closeButton.closest('.buno-plus-upsell-sidebar');
          if (sidebarElement) {
            const sectionId = sidebarElement.dataset.sectionId;
            if (sectionId) {
              this.hideUpsell(sectionId);
            }
          }
        }
      });

      // Listen for sidebar overlay clicks
      document.addEventListener('click', (event) => {
        const overlay = event.target.closest('.buno-plus-upsell-sidebar .overlay');
        if (overlay) {
          event.preventDefault();
          const sidebarElement = overlay.closest('.buno-plus-upsell-sidebar');
          if (sidebarElement) {
            const sectionId = sidebarElement.dataset.sectionId;
            if (sectionId) {
              this.hideUpsell(sectionId);
            }
          }
        }
      });
    }

    /**
     * Show upsell content for a specific section
     * @param {string} sectionId - The section ID
     * @param {string} context - The context ('cart-drawer' or 'cart-page')
     */
    showUpsell(sectionId, context) {
      // console.log('🛍️ [DEBUG] Starting showUpsell - Section:', sectionId, 'Context:', context);

      // Check if sidebar mode is being used
      const sidebarElement = document.getElementById(`buno-plus-upsell-sidebar-${sectionId}`);
      const isSidebarMode = !!sidebarElement;
      
      // console.log('🛍️ [DEBUG] Sidebar mode detected:', isSidebarMode);

      if (isSidebarMode) {
        return this.showSidebarUpsell(sectionId, context);
      }

      // Debug: Check if upsell content exists (inline mode)
      const upsellContent = document.getElementById(`buno-plus-upsell-content-${sectionId}`);
      // console.log('🛍️ [DEBUG] Upsell content element:', upsellContent);
      if (!upsellContent) {
        console.warn('🛍️ [ERROR] Upsell content not found for section:', sectionId);
        return false;
      }

      // Check if product is unavailable
      if (upsellContent.dataset.productUnavailable === 'true') {
        console.warn('🛍️ [ERROR] Buno Plus product is unavailable for section:', sectionId);
        this.showErrorMessage('Buno Plus is currently unavailable. Please contact support.');
        return false;
      }

      // Debug: Check if cart container exists
      const cartContainer = this.getCartContainer(sectionId, context);
      // console.log('🛍️ [DEBUG] Cart container:', cartContainer);
      if (!cartContainer) {
        console.warn('🛍️ [ERROR] Cart container not found for section:', sectionId);
        return false;
      }

      // Hide original cart content
      // console.log('🛍️ [DEBUG] Hiding original cart content...');
      this.hideOriginalContent(sectionId, context);

      // Show upsell content panel with enhanced positioning fixes
      // console.log('🛍️ [DEBUG] Showing upsell content panel...');
      if (context === 'cart-drawer') {
        const upsellPanel = document.querySelector(`#BunoPlusUpsell-${sectionId}`);
        // console.log('🛍️ [DEBUG] Upsell panel element:', upsellPanel);
        
        if (upsellPanel) {
          // console.log('🛍️ [DEBUG] Before panel show - display:', upsellPanel.style.display, 'opacity:', upsellPanel.style.opacity, 'classes:', upsellPanel.className);
          
          // ENHANCED VISIBILITY MANAGEMENT: Apply systematic positioning fixes
          this.applyVisibilityFixes(upsellPanel);
          
          // Set panel to visible with all necessary overrides
          upsellPanel.style.display = 'flex';
          upsellPanel.style.opacity = '1';
          upsellPanel.style.visibility = 'visible';
          upsellPanel.style.pointerEvents = 'auto';
          upsellPanel.classList.remove('opacity-0');
          upsellPanel.classList.add('opacity-100', 'buno-plus-upsell-visible');
          
          // console.log('🛍️ [DEBUG] After panel show - display:', upsellPanel.style.display, 'opacity:', upsellPanel.style.opacity, 'classes:', upsellPanel.className);
        } else {
          console.error('🛍️ [ERROR] Upsell panel not found with ID:', `BunoPlusUpsell-${sectionId}`);
        }
      } else {
        const upsellContainer = document.querySelector(`#BunoPlusUpsell-${sectionId}`);
        // console.log('🛍️ [DEBUG] Upsell container element:', upsellContainer);
        
        if (upsellContainer) {
          this.applyVisibilityFixes(upsellContainer);
          upsellContainer.style.display = 'block';
          upsellContainer.classList.add('buno-plus-upsell-visible');
        } else {
          console.error('🛍️ [ERROR] Upsell container not found with ID:', `BunoPlusUpsell-${sectionId}`);
        }
      }
      
      // Show upsell content itself with enhanced visibility management
      // console.log('🛍️ [DEBUG] Setting upsell content display and applying fixes...');
      this.applyVisibilityFixes(upsellContent);
      upsellContent.style.display = 'flex';
      upsellContent.classList.add('buno-plus-upsell-visible');

      // Handle header replacement if enabled
      // console.log('🛍️ [DEBUG] Checking for header replacement...');
      if (context === 'cart-drawer' && this.checkReplaceHeaderFlag(sectionId)) {
        // console.log('🛍️ [DEBUG] Header replacement enabled, switching headers...');
        this.showUpsellHeader(sectionId);
      }
      
      // Enhanced debugging with validation
      setTimeout(() => {
        const panel = document.querySelector(`#BunoPlusUpsell-${sectionId}`);
        this.validatePositioning(upsellContent, 'Upsell Content');
        this.validatePositioning(panel, 'Upsell Panel');
        
        // Additional debugging info
        const rect = upsellContent.getBoundingClientRect();
        // console.log('🛍️ [DEBUG] Final upsell positioning state:', {
        //   contentVisible: rect.width > 0 && rect.height > 0,
        //   panelVisible: panel ? panel.getBoundingClientRect().width > 0 : false,
        //   zIndexHierarchy: {
        //     content: getComputedStyle(upsellContent).zIndex,
        //     panel: panel ? getComputedStyle(panel).zIndex : 'N/A'
        //   }
        // });
      }, 100);
      
      // Track active upsell
      this.activeUpsells.set(sectionId, {
        context,
        timestamp: Date.now()
      });

      // console.log('🛍️ [DEBUG] Upsell successfully shown. Active upsells:', this.activeUpsells);

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart-upsell:shown', {
        detail: { sectionId, context }
      }));

      return true;
    }

    /**
     * Show sidebar upsell content
     * @param {string} sectionId - The section ID
     * @param {string} context - The context ('cart-drawer' or 'cart-page')
     */
    showSidebarUpsell(sectionId, context) {
      // console.log('🛍️ [DEBUG] Starting showSidebarUpsell - Section:', sectionId, 'Context:', context);

      const sidebarElement = document.getElementById(`buno-plus-upsell-sidebar-${sectionId}`);
      if (!sidebarElement) {
        console.warn('🛍️ [ERROR] Sidebar upsell element not found for section:', sectionId);
        return false;
      }

      // console.log('🛍️ [DEBUG] Opening sidebar upsell...');

      // First remove hidden and make visible
      sidebarElement.removeAttribute('hidden');
      sidebarElement.setAttribute('open', '');
      sidebarElement.style.pointerEvents = 'auto';

      // Trigger drawer animation by adding active attribute
      // Use a small delay to ensure the element is rendered first
      requestAnimationFrame(() => {
        // console.log('🛍️ [DEBUG] Activating sidebar drawer animation...');
        sidebarElement.setAttribute('active', '');
        
        // // console.log('🛍️ [DEBUG] Sidebar activation completed - checking element state:', {
        //   hasActive: sidebarElement.hasAttribute('active'),
        //   hasOpen: sidebarElement.hasAttribute('open'),
        //   display: getComputedStyle(sidebarElement).display,
        //   transform: getComputedStyle(sidebarElement.querySelector('.drawer__inner')).transform,
        //   translateX: getComputedStyle(sidebarElement.querySelector('.drawer__inner')).getPropertyValue('--tw-translate-x')
        // });
      });

      // Track active upsell
      this.activeUpsells.set(sectionId, {
        context,
        displayMode: 'sidebar',
        timestamp: Date.now()
      });

      // console.log('🛍️ [DEBUG] Sidebar upsell successfully shown. Active upsells:', this.activeUpsells);

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart-upsell:shown', {
        detail: { sectionId, context, displayMode: 'sidebar' }
      }));

      return true;
    }

    /**
     * Hide upsell content and restore original cart view
     * @param {string} sectionId - The section ID
     */
    hideUpsell(sectionId) {
      console.log('🛍️ Hiding upsell for section:', sectionId);

      const upsellInfo = this.activeUpsells.get(sectionId);
      if (!upsellInfo) {
        console.warn('🛍️ No active upsell found for section:', sectionId);
        return false;
      }

      // Check if this is a sidebar mode upsell
      if (upsellInfo.displayMode === 'sidebar') {
        return this.hideSidebarUpsell(sectionId, upsellInfo);
      }

      const upsellContent = document.getElementById(`buno-plus-upsell-content-${sectionId}`);
      if (upsellContent) {
        upsellContent.style.display = 'none';
        upsellContent.classList.remove('buno-plus-upsell-visible');
      }

      // Hide upsell content panel with enhanced cleanup
      if (upsellInfo.context === 'cart-drawer') {
        const upsellPanel = document.querySelector(`#BunoPlusUpsell-${sectionId}`);
        if (upsellPanel) {
          upsellPanel.style.display = 'none';
          upsellPanel.style.opacity = '0';
          upsellPanel.style.visibility = 'hidden';
          upsellPanel.classList.remove('opacity-100', 'buno-plus-upsell-visible');
          upsellPanel.classList.add('opacity-0');
        }
      } else {
        const upsellContainer = document.querySelector(`#BunoPlusUpsell-${sectionId}`);
        if (upsellContainer) {
          upsellContainer.style.display = 'none';
          upsellContainer.classList.remove('buno-plus-upsell-visible');
        }
      }

      // Handle header restoration if it was replaced
      // console.log('🛍️ [DEBUG] Checking for header restoration...');
      if (upsellInfo.context === 'cart-drawer' && this.checkReplaceHeaderFlag(sectionId)) {
        // console.log('🛍️ [DEBUG] Header was replaced, restoring default header...');
        this.hideUpsellHeader(sectionId);
      }

      // Restore original content
      this.restoreOriginalContent(sectionId, upsellInfo.context);

      // Remove from active upsells
      this.activeUpsells.delete(sectionId);

      // Trigger cart re-render to show updated state (e.g., newly added bunoplus)
      if (window.CartDataManager && typeof window.CartDataManager.refreshCart === 'function') {
        window.CartDataManager.refreshCart();
      } else if (document.querySelector('cart-drawer')) {
        // Fallback: dispatch cart refresh event
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart-upsell:hidden', {
        detail: { sectionId, context: upsellInfo.context }
      }));

      return true;
    }

    /**
     * Hide sidebar upsell content
     * @param {string} sectionId - The section ID
     * @param {object} upsellInfo - The upsell information
     */
    hideSidebarUpsell(sectionId, upsellInfo) {
      // console.log('🛍️ [DEBUG] Hiding sidebar upsell for section:', sectionId);

      const sidebarElement = document.getElementById(`buno-plus-upsell-sidebar-${sectionId}`);
      if (!sidebarElement) {
        console.warn('🛍️ [ERROR] Sidebar upsell element not found for section:', sectionId);
        return false;
      }

      // console.log('🛍️ [DEBUG] Closing sidebar upsell...');

      // Remove active attribute to trigger closing animation
      sidebarElement.removeAttribute('active');
      
      // console.log('🛍️ [DEBUG] Removed active attribute, drawer should start closing animation');

      // Wait for animation to complete, then fully hide
      setTimeout(() => {
        sidebarElement.removeAttribute('open');
        sidebarElement.setAttribute('hidden', '');
        sidebarElement.style.pointerEvents = 'none';
        
        // console.log('🛍️ [DEBUG] Sidebar fully hidden after animation');
      }, 350); // Match the CSS transition duration

      // Remove from active upsells
      this.activeUpsells.delete(sectionId);

      // console.log('🛍️ [DEBUG] Sidebar upsell hidden successfully');

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart-upsell:hidden', {
        detail: { sectionId, context: upsellInfo.context, displayMode: 'sidebar' }
      }));

      return true;
    }

    /**
     * Get the appropriate cart container based on context
     */
    getCartContainer(sectionId, context) {
      if (context === 'cart-drawer') {
        return document.querySelector(`#CartDrawer`);
      } else if (context === 'cart-page') {
        return document.querySelector(`#MainCart-${sectionId}`);
      }
      return null;
    }


    /**
     * Hide original cart content
     */
    hideOriginalContent(sectionId, context) {
      // console.log('🛍️ [DEBUG] Hiding original content for section:', sectionId, 'context:', context);
      
      if (context === 'cart-drawer') {
        const miniCart = document.querySelector(`#MiniCart-${sectionId}`);
        // console.log('🛍️ [DEBUG] MiniCart element:', miniCart);
        
        if (miniCart) {
          // console.log('🛍️ [DEBUG] Before hide - MiniCart display:', miniCart.style.display, 'classes:', miniCart.className);
          
          miniCart.style.display = 'none';
          miniCart.classList.remove('opacity-100');
          miniCart.classList.add('opacity-0');
          
          // console.log('🛍️ [DEBUG] After hide - MiniCart display:', miniCart.style.display, 'classes:', miniCart.className);
        } else {
          console.error('🛍️ [ERROR] MiniCart not found with ID:', `MiniCart-${sectionId}`);
        }
      } else if (context === 'cart-page') {
        const container = this.getCartContainer(sectionId, context);
        // console.log('🛍️ [DEBUG] Cart page container:', container);
        
        const cartContent = container?.querySelector('.cart__content');
        // console.log('🛍️ [DEBUG] Cart content element:', cartContent);
        
        if (cartContent) {
          // console.log('🛍️ [DEBUG] Before hide - Cart content display:', cartContent.style.display);
          cartContent.style.display = 'none';
          // console.log('🛍️ [DEBUG] After hide - Cart content display:', cartContent.style.display);
        } else {
          console.error('🛍️ [ERROR] Cart content not found');
        }
      }
    }

    /**
     * Restore original cart content
     */
    restoreOriginalContent(sectionId, context) {
      // console.log('🛍️ [DEBUG] Restoring original content for section:', sectionId, 'context:', context);
      
      if (context === 'cart-drawer') {
        const miniCart = document.querySelector(`#MiniCart-${sectionId}`);
        // console.log('🛍️ [DEBUG] Restoring MiniCart element:', miniCart);
        
        if (miniCart) {
          // console.log('🛍️ [DEBUG] Before restore - MiniCart display:', miniCart.style.display, 'classes:', miniCart.className);
          
          miniCart.style.display = 'flex';
          miniCart.classList.remove('opacity-0');
          miniCart.classList.add('opacity-100');
          
          // console.log('🛍️ [DEBUG] After restore - MiniCart display:', miniCart.style.display, 'classes:', miniCart.className);
        } else {
          console.error('🛍️ [ERROR] MiniCart not found for restore with ID:', `MiniCart-${sectionId}`);
        }
      } else if (context === 'cart-page') {
        const container = this.getCartContainer(sectionId, context);
        // console.log('🛍️ [DEBUG] Restoring cart page container:', container);
        
        const cartContent = container?.querySelector('.cart__content');
        // console.log('🛍️ [DEBUG] Restoring cart content element:', cartContent);
        
        if (cartContent) {
          // console.log('🛍️ [DEBUG] Before restore - Cart content display:', cartContent.style.display);
          cartContent.style.display = 'flex';
          // console.log('🛍️ [DEBUG] After restore - Cart content display:', cartContent.style.display);
        } else {
          console.error('🛍️ [ERROR] Cart content not found for restore');
        }
      }
    }

    /**
     * Check if upsell content has replace_header flag enabled
     */
    checkReplaceHeaderFlag(sectionId) {
      const upsellContent = document.getElementById(`buno-plus-upsell-content-${sectionId}`);
      if (!upsellContent) return false;
      
      const replaceHeader = upsellContent.dataset.replaceHeader;
      return replaceHeader === 'true';
    }

    /**
     * Show upsell header and hide default header
     */
    showUpsellHeader(sectionId) {
      // console.log('🛍️ [DEBUG] Switching to upsell header for section:', sectionId);
      
      const defaultHeader = document.getElementById(`default-cart-header-${sectionId}`);
      const upsellHeader = document.getElementById(`upsell-cart-header-${sectionId}`);
      
      if (!defaultHeader || !upsellHeader) {
        // console.warn('🛍️ [DEBUG] Header elements not found - default:', !!defaultHeader, 'upsell:', !!upsellHeader);
        return false;
      }
      
      // Hide default header
      defaultHeader.style.display = 'none';
      
      // Show upsell header  
      upsellHeader.style.display = 'flex';
      
      // console.log('🛍️ [DEBUG] Header switch completed successfully');
      return true;
    }

    /**
     * Hide upsell header and show default header
     */
    hideUpsellHeader(sectionId) {
      // console.log('🛍️ [DEBUG] Switching to default header for section:', sectionId);
      
      const defaultHeader = document.getElementById(`default-cart-header-${sectionId}`);
      const upsellHeader = document.getElementById(`upsell-cart-header-${sectionId}`);
      
      if (!defaultHeader || !upsellHeader) {
        // console.warn('🛍️ [DEBUG] Header elements not found - default:', !!defaultHeader, 'upsell:', !!upsellHeader);
        return false;
      }
      
      // Show default header
      defaultHeader.style.display = 'flex';
      
      // Hide upsell header
      upsellHeader.style.display = 'none';
      
      // console.log('🛍️ [DEBUG] Header restoration completed successfully');
      return true;
    }

    /**
     * Handle upsell action buttons
     */
    async handleUpsellAction(button) {
      const action = button.dataset.action;
      // Try to find section ID from either inline content or sidebar container
      let sectionId = null;
      const upsellElement = button.closest('.buno-plus-upsell-content');
      const sidebarElement = button.closest('.buno-plus-upsell-sidebar');
      
      if (upsellElement) {
        sectionId = upsellElement.dataset.sectionId;
      } else if (sidebarElement) {
        sectionId = sidebarElement.dataset.sectionId;
      }
      
      if (!sectionId) {
        console.error('🛍️ [ERROR] Could not find section ID for upsell action');
        return;
      }

      // Add loading state - show btn-loader
      const btnLoader = button.querySelector('.btn-loader');
      if (btnLoader) {
        btnLoader.style.opacity = '1';
        btnLoader.style.visibility = 'visible';
      }
      button.disabled = true;

      try {
        if (action === 'add-and-checkout') {
          await this.handleAddBunoPlusAndCheckout(button, sectionId);
        } else if (action === 'continue-without') {
          await this.handleContinueWithout(button, sectionId);
        }
      } catch (error) {
        console.error('🛍️ Error handling upsell action:', error);
      }
    }

    /**
     * Handle adding Buno Plus and proceeding to checkout
     */
    async handleAddBunoPlusAndCheckout(button, sectionId) {
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;

      console.log('🛍️ Adding Buno Plus and proceeding to checkout:', { productId, variantId });

      try {
        // Use CartDataManager if available, otherwise fallback to direct fetch
        if (window.CartDataManager) {
          console.log('🛍️ Using CartDataManager to add Buno Plus and checkout');
          
          // Add Buno Plus and proceed to checkout in one operation
          await window.CartDataManager.proceedToCheckout({
            variantId: variantId,
            quantity: 1
          });
        } else {
          console.log('🛍️ CartDataManager not available, using fallback method');
          
          // Fallback to old method
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: variantId,
              quantity: 1,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to add Buno Plus to cart');
          }
          // Proceed to checkout
          this.proceedToCheckout();
        }

      } catch (error) {
        console.error('🛍️ Error adding Buno Plus:', error);
        
        // Show user-friendly error message
        this.showErrorMessage(
          'Unable to add Buno Plus to your cart. Please try again or continue without it.',
          button
        );
        
        // Try to continue without Buno Plus as fallback
        setTimeout(() => {
          if (confirm('Would you like to continue to checkout without Buno Plus?')) {
            this.handleContinueWithout(null, sectionId);
          }
        }, 2000);
      }
      window.ButtonLoadingAnimation.stop(button);
    }

    /**
     * Handle continuing without Buno Plus
     */
    async handleContinueWithout(button, sectionId) {
      console.log('🛍️ Continuing without Buno Plus');
      
      // Small delay to allow UI to update, then proceed to checkout
      setTimeout(() => {
        if (window.CartDataManager) {
          console.log('🛍️ Using CartDataManager to proceed to checkout');
          if (button) {
            window.ButtonLoadingAnimation.stop(button);
          }
          
          window.CartDataManager.proceedToCheckout();
        } else {
          console.log('🛍️ CartDataManager not available, using fallback checkout');
          if (button) {
            window.ButtonLoadingAnimation.stop(button);
          }
          this.proceedToCheckout();
        }
      }, 300);
      
    }

    /**
     * Proceed to checkout
     */
    proceedToCheckout() {
      
      console.log('🛍️ Proceeding to checkout...');
      
      // Find the cart form and submit it
      const cartForm = document.getElementById('cart');
      if (cartForm) {
        // Create a hidden input for the checkout action
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '';
        cartForm.appendChild(checkoutInput);
        
        // Submit the form
        cartForm.submit();
      } else {
        console.error('🛍️ Cart form not found');
        // Fallback: redirect to checkout
        window.location.href = '/checkout';
      }
    }

    /**
     * Event handler for show upsell custom event
     */
    handleShowUpsell(event) {
      const { sectionId, context } = event.detail;
      this.showUpsell(sectionId, context);
    }

    /**
     * Event handler for hide upsell custom event
     */
    handleHideUpsell(event) {
      const { sectionId } = event.detail;
      this.hideUpsell(sectionId);
    }

    /**
     * Check if upsell is currently active for a section
     */
    isUpsellActive(sectionId) {
      return this.activeUpsells.has(sectionId);
    }

    /**
     * Get all active upsells
     */
    getActiveUpsells() {
      return Array.from(this.activeUpsells.entries()).map(([sectionId, info]) => ({
        sectionId,
        ...info
      }));
    }

    /**
     * Apply systematic visibility fixes to element
     * This addresses all the root causes of positioning issues
     */
    applyVisibilityFixes(element) {
      if (!element) return;
      
      // console.log('🛍️ [DEBUG] Applying visibility fixes to element:', element);
      
      // Force proper positioning and z-index
      element.style.position = 'relative';
      element.style.zIndex = '100';
      
      // Override any opacity/visibility inheritance
      element.style.opacity = '1';
      element.style.visibility = 'visible';
      element.style.pointerEvents = 'auto';
      
      // Create proper stacking context
      element.style.transform = 'translateZ(0)';
      element.style.isolation = 'isolate';
      
      // Fix flex positioning issues
      if (element.closest('.drawer__panel')) {
        element.style.flex = '1 1 auto';
        element.style.alignSelf = 'stretch';
        element.style.justifySelf = 'stretch';
      }
      
      // Override any problematic inherited styles
      element.style.display = 'flex';
      element.style.backgroundColor = 'white';
      
      // console.log('🛍️ [DEBUG] Visibility fixes applied successfully');
    }

    /**
     * Validate element positioning for debugging
     */
    validatePositioning(element, elementName = 'element') {
      if (!element) {
        console.warn(`🛍️ [VALIDATION] ${elementName} not found`);
        return false;
      }
      
      const rect = element.getBoundingClientRect();
      const computed = getComputedStyle(element);
      
      const validationResults = {
        visible: rect.width > 0 && rect.height > 0,
        inViewport: rect.top >= 0 && rect.left >= 0,
        display: computed.display !== 'none',
        opacity: parseFloat(computed.opacity) > 0,
        zIndex: computed.zIndex,
        position: computed.position,
        transform: computed.transform
      };
      
      console.log(`🛍️ [VALIDATION] ${elementName}:`, validationResults);
      
      return validationResults.visible && validationResults.display && validationResults.opacity;
    }

    /**
     * Show user-friendly error message
     * @param {string} message - Error message to display
     * @param {HTMLElement} button - Button element to show error near
     */
    showErrorMessage(message, button = null) {
      console.log('🛍️ [ERROR] Showing error message:', message);

      // Try to find a good place to show the error
      const targetElement = button || document.querySelector('.buno-plus-upsell-content') || document.body;
      
      // Create error message element
      const errorDiv = document.createElement('div');
      errorDiv.className = 'buno-plus-error-message';
      errorDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #dc3545;
        color: white;
        padding: 15px 20px;
        border-radius: 8px;
        z-index: 10000;
        max-width: 300px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        font-size: 14px;
        line-height: 1.4;
      `;
      errorDiv.textContent = message;

      // Add close button
      const closeBtn = document.createElement('button');
      closeBtn.innerHTML = '×';
      closeBtn.style.cssText = `
        position: absolute;
        top: 8px;
        right: 12px;
        background: none;
        border: none;
        color: white;
        font-size: 18px;
        cursor: pointer;
        padding: 0;
        line-height: 1;
      `;
      closeBtn.onclick = () => errorDiv.remove();
      errorDiv.appendChild(closeBtn);

      // Add to document
      document.body.appendChild(errorDiv);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.remove();
        }
      }, 5000);
    }

    /**
     * Debug utility function to manually trigger upsell for testing
     * Can be called from browser console: window.CartUpsellManager.triggerDebugUpsell()
     */
    triggerDebugUpsell() {
      // console.log('🛍️ [DEBUG] Manually triggering debug upsell...');
      this.autoEnableDebugUpsell();
    }

    /**
     * Debug utility to add debug_modal parameter to current URL
     */
    enableDebugMode() {
      const url = new URL(window.location);
      url.searchParams.set('debug_modal', '1');
      window.location.href = url.toString();
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    // console.log('🛍️ [DEBUG] DOM still loading, waiting for DOMContentLoaded...');
    document.addEventListener('DOMContentLoaded', () => {
      // console.log('🛍️ [DEBUG] DOM loaded, initializing CartUpsellManager...');
      window.CartUpsellManager = new CartUpsellManager();
      // console.log('🛍️ [DEBUG] CartUpsellManager assigned to window:', !!window.CartUpsellManager);
    });
  } else {
    // console.log('🛍️ [DEBUG] DOM already loaded, initializing CartUpsellManager immediately...');
    window.CartUpsellManager = new CartUpsellManager();
    // console.log('🛍️ [DEBUG] CartUpsellManager assigned to window:', !!window.CartUpsellManager);
  }

})();